import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Link2, Key, Copy, Check, Send, AlertTriangle,
  ToggleLeft, ToggleRight, ShoppingBag, Calendar, TrendingUp,
  Zap, Globe, ChevronDown, ChevronUp, ExternalLink, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateUtils';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  acompte_paye: { color: '#22C55E', label: 'Confirmé' },
  confirme:     { color: '#22C55E', label: 'Confirmé' },
  annule:       { color: 'var(--admin-red)', label: 'Annulé'   },
  default:      { color: 'var(--admin-amber)', label: 'En cours' },
};

export default function AdminPlaceToLocal() {
  const [stats,     setStats]     = useState(null);
  const [config,    setConfig]    = useState({});
  const [bookings,  setBookings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [webhookUrl,    setWebhookUrl]    = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [b2bDiscount,   setB2bDiscount]   = useState(15);
  const [allowedZones,  setAllowedZones]  = useState('figeac,capdenac,cahors');
  const [newKey,        setNewKey]        = useState(null);
  const [keyCopied,     setKeyCopied]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [expandedHook,  setExpandedHook]  = useState(null);

  const token = getToken();
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, cfgRes, bkRes] = await Promise.all([
        fetch(`${BACKEND}/api/admin/ptl/stats`,    { headers: h }),
        fetch(`${BACKEND}/api/admin/ptl/config`,   { headers: h }),
        fetch(`${BACKEND}/api/admin/ptl/bookings`, { headers: h }),
      ]);
      const [s, c, b] = await Promise.all([statsRes.json(), cfgRes.json(), bkRes.json()]);
      setStats(s);
      setConfig(c);
      setBookings(b.bookings || []);
      setWebhookUrl(c.webhook_url || '');
      setWebhookSecret(c.webhook_secret || '');
      setB2bDiscount(c.b2b_discount_pct ?? 15);
      setAllowedZones((c.allowed_zones || ['figeac','capdenac','cahors']).join(','));
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND}/api/admin/ptl/config`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          webhook_url:      webhookUrl,
          webhook_secret:   webhookSecret,
          b2b_discount_pct: parseInt(b2bDiscount),
          allowed_zones:    allowedZones.split(',').map(z => z.trim()).filter(Boolean),
        }),
      });
      toast.success('Configuration sauvegardée');
      load();
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    await fetch(`${BACKEND}/api/admin/ptl/config`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ active: !config.active }),
    });
    load();
    toast.success(config.active ? 'Intégration désactivée' : 'Intégration activée');
  };

  const generateKey = async () => {
    const res = await fetch(`${BACKEND}/api/admin/ptl/generate-key`, { method: 'POST', headers: h });
    const data = await res.json();
    setNewKey(data.api_key);
    toast.success('Nouvelle clé générée — copiez-la maintenant !');
    load();
  };

  const copyKey = () => {
    if (!newKey) return;
    navigator.clipboard.writeText(newKey).then(() => {
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 3000);
      toast.success('Clé copiée !');
    });
  };

  const testWebhook = async () => {
    await fetch(`${BACKEND}/api/admin/ptl/test-webhook`, { method: 'POST', headers: h });
    toast.success('Webhook de test envoyé');
    setTimeout(load, 2000);
  };

  const TABS = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'config',    label: '⚙️ Configuration' },
    { id: 'bookings',  label: `📦 Réservations PTL (${bookings.length})` },
    { id: 'webhooks',  label: '🔔 Webhooks' },
    { id: 'doc',       label: '📄 Documentation API' },
  ];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Place to Local</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Intégration B2B catalogue et réservations</p>
      </div>
 className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--admin-teal-dim)' }}>
              <Link2 size={20} style={{ color: 'var(--admin-teal)' }} />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>
                Place to Local
              </h1>
              <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                Liaison stratégique Show Événement ↔ Place to Local
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ background: config.active ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)',
              border: `1px solid ${config.active ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.3)'}` }}>
            <div className="w-2 h-2 rounded-full"
              style={{ background: config.active ? '#22C55E' : '#EF4444',
                animation: config.active ? 'ping 2s infinite' : 'none' }} />
            <span className="text-xs font-bold"
              style={{ color: config.active ? '#16A34A' : '#EF4444' }}>
              {config.active ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <button onClick={toggleActive}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-muted)' }}>
            {config.active ? <ToggleRight size={28} style={{ color:'#22C55E' }} /> : <ToggleLeft size={28} />}
          </button>
          <button onClick={() => load()} className="p-2 rounded-xl"
            style={{ background:'var(--bg)', border:'1px solid var(--border)', cursor:'pointer' }}>
            <RefreshCw size={15} style={{ color:'var(--text-muted)' }} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Réservations PTL', value: stats.total_bookings_ptl, color: 'var(--admin-teal)', icon: ShoppingBag },
            { label: 'CA depuis PTL',    value: `${stats.ca_ptl?.toFixed(0)}€`, color: 'var(--admin-gold)', icon: TrendingUp },
            { label: 'Webhooks envoyés', value: stats.webhooks_total, color: 'var(--admin-purple)', icon: Zap },
            { label: 'Santé webhook',    value: stats.webhook_health, color: stats.webhooks_errors > 0 ? '#EF4444' : '#22C55E', icon: AlertTriangle },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card-premium p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={13} style={{ color: s.color }} />
                  <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
                </div>
                <p className="font-display text-xl font-bold truncate" style={{ color: s.color }}>{s.value}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-5 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="px-4 py-3 text-sm font-semibold border-b-2 flex-shrink-0"
            style={{ borderColor: activeTab===tab.id ? '#5DBCBF' : 'transparent',
              color: activeTab===tab.id ? '#5DBCBF' : 'var(--text-muted)',
              background:'none', cursor:'pointer' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── DASHBOARD ── */}
      {activeTab === 'dashboard' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Architecture */}
          <div className="card-premium p-5" style={{ background:'#0B1E20' }}>
            <p className="font-bold text-sm mb-4" style={{ color:'#D4AF37' }}>🏗️ Architecture de la liaison</p>
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              {[
                { name:'Show Événement', role:'Source maître', emoji:'⭐', color:'#D4AF37', items:['Disponibilités','Réservations','Livraisons','Stocks','Tarifs'] },
                { name:'API de Sync',    role:'Bridge temps réel', emoji:'⚡', color:'#5DBCBF', items:['Anti-doublon','Webhooks','Disponibilités bulk','Auth API Key'] },
                { name:'Place to Local', role:'Vitrine territoriale', emoji:'📍', color:'#F4A7B2', items:['Vitrine produits','Agenda central','B2B -15%','Pôles locaux'] },
              ].map((node, i) => (
                <div key={i} className="p-4 rounded-2xl" style={{ background:'rgba(255,255,255,0.05)' }}>
                  <div className="text-3xl mb-2">{node.emoji}</div>
                  <p className="font-bold text-sm mb-0.5" style={{ color: node.color }}>{node.name}</p>
                  <p className="text-xs mb-3" style={{ color:'rgba(255,255,255,0.4)' }}>{node.role}</p>
                  {node.items.map(item => (
                    <p key={item} className="text-xs mb-1" style={{ color:'rgba(255,255,255,0.6)' }}>
                      • {item}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Derniers webhooks */}
          {stats?.last_webhooks?.length > 0 && (
            <div className="card-premium overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
                <p className="font-bold text-sm" style={{ color:'var(--ink)' }}>Derniers webhooks envoyés</p>
              </div>
              {stats.last_webhooks.map((w, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor:'var(--border)' }}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: w.status >= 200 && w.status < 300 ? '#22C55E' : w.status === 0 ? '#EF4444' : '#F59E0B' }} />
                  <span className="text-xs font-semibold" style={{ color:'var(--ink)' }}>{w.event}</span>
                  <span className="text-xs flex-1 truncate" style={{ color:'var(--text-muted)' }}>{w.url?.slice(0,50)}</span>
                  <span className="text-xs font-bold"
                    style={{ color: w.status >= 200 && w.status < 300 ? '#22C55E' : '#EF4444' }}>
                    HTTP {w.status || 'ERR'}
                  </span>
                  <span className="text-xs" style={{ color:'var(--text-muted)' }}>
                    {w.created_at ? new Date(w.created_at).toLocaleTimeString('fr-FR') : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CONFIGURATION ── */}
      {activeTab === 'config' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Clé API */}
          <div className="card-premium p-5">
            <p className="font-bold text-sm mb-1" style={{ color:'var(--ink)' }}>
              <Key size={14} className="inline mr-1.5" style={{ color:'#8B5CF6' }} />
              Clé API Place to Local
            </p>
            <p className="text-xs mb-4" style={{ color:'var(--text-muted)' }}>
              PTL envoie cette clé dans le header <code>X-PTL-API-Key</code> de chaque requête.
            </p>
            {config.api_key ? (
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'var(--bg)', border:'1px solid var(--border)' }}>
                <code className="flex-1 text-xs font-mono" style={{ color:'var(--ink)' }}>{config.api_key}</code>
                <span className="text-xs" style={{ color:'var(--text-muted)' }}>Masquée pour sécurité</span>
              </div>
            ) : (
              <p className="text-xs" style={{ color:'#F59E0B' }}>Aucune clé configurée</p>
            )}
            {newKey && (
              <div className="mt-3 p-3 rounded-xl" style={{ background:'rgba(34,197,94,.06)', border:'1px solid rgba(34,197,94,.25)' }}>
                <p className="text-xs font-bold mb-2" style={{ color:'#16A34A' }}>
                  ⚠️ Nouvelle clé — copiez-la maintenant, elle ne sera plus affichée en clair !
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono break-all" style={{ color:'#1F2937' }}>{newKey}</code>
                  <button onClick={copyKey}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
                    style={{ background: keyCopied ? '#22C55E' : '#D4AF37', color:'#0B1E20', border:'none', cursor:'pointer' }}>
                    {keyCopied ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
                  </button>
                </div>
              </div>
            )}
            <button onClick={generateKey} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold"
              style={{ background:'rgba(139,92,246,.1)', color:'#8B5CF6', border:'1px solid rgba(139,92,246,.3)', cursor:'pointer' }}>
              <Key size={13} /> Générer une nouvelle clé
            </button>
          </div>

          {/* Webhook */}
          <div className="card-premium p-5">
            <p className="font-bold text-sm mb-1" style={{ color:'var(--ink)' }}>
              <Zap size={14} className="inline mr-1.5" style={{ color:'#F59E0B' }} />
              Webhook sortant (SE → PTL)
            </p>
            <p className="text-xs mb-4" style={{ color:'var(--text-muted)' }}>
              SE notifie PTL automatiquement à chaque booking, annulation ou livraison planifiée.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color:'var(--ink)' }}>URL Webhook PTL</label>
                <input value={webhookUrl} onChange={e=>setWebhookUrl(e.target.value)}
                  placeholder="https://www.place-to-local.fr/webhooks/show-evenement"
                  className="w-full px-3 py-2.5 rounded-xl text-sm"
                  style={{ border:'1px solid var(--border)', background:'#FFF', color:'var(--ink)', outline:'none' }} />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1.5" style={{ color:'var(--ink)' }}>Secret HMAC (optionnel)</label>
                <input value={webhookSecret} onChange={e=>setWebhookSecret(e.target.value)}
                  placeholder="Secret partagé pour vérification HMAC-SHA256"
                  type="password"
                  className="w-full px-3 py-2.5 rounded-xl text-sm"
                  style={{ border:'1px solid var(--border)', background:'#FFF', color:'var(--ink)', outline:'none' }} />
              </div>
            </div>
          </div>

          {/* B2B */}
          <div className="card-premium p-5">
            <p className="font-bold text-sm mb-1" style={{ color:'var(--ink)' }}>
              <Percent size={14} className="inline mr-1.5" style={{ color:'#D4AF37' }} />
              Tarification B2B
            </p>
            <p className="text-xs mb-4" style={{ color:'var(--text-muted)' }}>
              Remise appliquée automatiquement pour les abonnés Market & Développement / Place to Local.
            </p>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color:'var(--ink)' }}>Remise (%)</label>
                <input type="number" value={b2bDiscount} onChange={e=>setB2bDiscount(e.target.value)}
                  min={0} max={50}
                  className="w-28 px-3 py-2 rounded-xl text-sm text-center"
                  style={{ border:'1px solid var(--border)', background:'#FFF', color:'var(--ink)', outline:'none' }} />
              </div>
              <div className="p-3 rounded-xl" style={{ background:'rgba(212,175,55,.06)', border:'1px solid rgba(212,175,55,.2)' }}>
                <p className="text-xs text-center" style={{ color:'var(--text-muted)' }}>Exemple</p>
                <p className="text-sm text-center font-bold" style={{ color:'var(--ink)' }}>
                  100€ → <span style={{ color:'#D4AF37' }}>{(100 * (1 - b2bDiscount/100)).toFixed(0)}€</span>
                </p>
              </div>
            </div>
          </div>

          {/* Pôles */}
          <div className="card-premium p-5">
            <p className="font-bold text-sm mb-1" style={{ color:'var(--ink)' }}>
              <Globe size={14} className="inline mr-1.5" style={{ color:'#5DBCBF' }} />
              Pôles locaux autorisés
            </p>
            <p className="text-xs mb-3" style={{ color:'var(--text-muted)' }}>
              Zones géographiques où les réservations PTL sont acceptées sans validation manuelle.
            </p>
            <input value={allowedZones} onChange={e=>setAllowedZones(e.target.value)}
              placeholder="figeac, capdenac, cahors"
              className="w-full px-3 py-2.5 rounded-xl text-sm"
              style={{ border:'1px solid var(--border)', background:'#FFF', color:'var(--ink)', outline:'none' }} />
            <p className="text-xs mt-1" style={{ color:'var(--text-muted)' }}>Séparés par virgule</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={saveConfig} disabled={saving}
              className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold"
              style={{ background:'#D4AF37', color:'#0B1E20', border:'none', cursor:'pointer', opacity:saving?0.7:1 }}>
              {saving ? 'Sauvegarde...' : '✓ Sauvegarder'}
            </button>
            <button onClick={testWebhook}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold"
              style={{ background:'var(--bg)', border:'1px solid var(--border)', color:'var(--ink)', cursor:'pointer' }}>
              <Send size={13} /> Tester webhook
            </button>
          </div>
        </div>
      )}

      {/* ── RÉSERVATIONS PTL ── */}
      {activeTab === 'bookings' && (
        <div>
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={36} style={{ color:'var(--border)', margin:'0 auto 12px' }} />
              <p className="text-sm" style={{ color:'var(--text-muted)' }}>Aucune réservation depuis Place to Local</p>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map(b => {
                const sc = STATUS_COLORS[b.statut] || STATUS_COLORS.default;
                return (
                  <div key={b.id} className="card-premium p-4 flex items-center gap-4">
                    <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: sc.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-bold text-sm" style={{ color:'var(--ink)' }}>{b.client_nom}</span>
                        {b.is_b2b && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                            style={{ background:'rgba(212,175,55,.12)', color:'#C58B19' }}>B2B -15%</span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background:`${sc.color}15`, color: sc.color }}>{sc.label}</span>
                      </div>
                      <p className="text-xs truncate" style={{ color:'var(--text-muted)' }}>
                        {b.numero} · {b.date_event} · {b.products?.join(', ')?.slice(0,60)}
                      </p>
                      {b.ptl_ref && (
                        <p className="text-xs" style={{ color:'#9CA3AF' }}>Réf. PTL : {b.ptl_ref}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm" style={{ color:'#D4AF37' }}>{b.total?.toFixed(0)}€</p>
                      <p className="text-xs" style={{ color:'var(--text-muted)' }}>
                        {b.created_at ? formatDate(b.created_at) : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── WEBHOOKS ── */}
      {activeTab === 'webhooks' && (
        <div>
          <div className="p-4 rounded-2xl mb-5" style={{ background:'rgba(139,92,246,.06)', border:'1px solid rgba(139,92,246,.2)' }}>
            <p className="text-sm font-bold mb-2" style={{ color:'#8B5CF6' }}>Events SE → PTL</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { event:'booking.created',   desc:'Produit réservé sur SE → PTL bloque le créneau' },
                { event:'booking.cancelled', desc:'Annulation → PTL libère le créneau' },
                { event:'delivery.scheduled',desc:'Livraison planifiée → PTL bloque le slot horaire' },
                { event:'test.ping',         desc:'Test de connectivité' },
              ].map(e => (
                <div key={e.event} className="p-3 rounded-xl" style={{ background:'rgba(255,255,255,0.5)', border:'1px solid var(--border)' }}>
                  <code className="block text-xs font-bold mb-1" style={{ color:'#8B5CF6' }}>{e.event}</code>
                  <p className="text-xs" style={{ color:'var(--text-muted)' }}>{e.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {stats?.last_webhooks?.length > 0 && (
            <div className="space-y-2">
              {stats.last_webhooks.map((w, i) => {
                const ok = w.status >= 200 && w.status < 300;
                return (
                  <div key={i} className="card-premium overflow-hidden">
                    <button onClick={() => setExpandedHook(expandedHook===i?null:i)}
                      className="w-full flex items-center gap-3 p-3 text-left"
                      style={{ cursor:'pointer', background:'none', border:'none' }}>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ background: ok ? '#22C55E' : '#EF4444' }} />
                      <span className="text-xs font-semibold flex-1" style={{ color:'var(--ink)' }}>{w.event}</span>
                      <span className="text-xs font-bold" style={{ color: ok ? '#22C55E' : '#EF4444' }}>
                        {w.status || 'ERR'}
                      </span>
                      <span className="text-xs" style={{ color:'var(--text-muted)' }}>
                        {w.created_at ? formatDateTime(w.created_at) : ''}
                      </span>
                      {expandedHook===i ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
                    </button>
                    {expandedHook===i && w.error && (
                      <div className="px-4 pb-3 text-xs" style={{ color:'#EF4444' }}>
                        Erreur : {w.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── DOCUMENTATION API ── */}
      {activeTab === 'doc' && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="p-4 rounded-2xl" style={{ background:'rgba(93,188,191,.06)', border:'1px solid rgba(93,188,191,.2)' }}>
            <p className="text-sm font-bold mb-1" style={{ color:'#0B4B4E' }}>Base URL</p>
            <code className="text-xs" style={{ color:'var(--ink)' }}>{BACKEND}/api/ptl/</code>
          </div>
          <div className="p-4 rounded-2xl" style={{ background:'rgba(93,188,191,.06)', border:'1px solid rgba(93,188,191,.2)' }}>
            <p className="text-sm font-bold mb-1" style={{ color:'#0B4B4E' }}>Authentification</p>
            <code className="text-xs" style={{ color:'var(--ink)' }}>Header: X-PTL-API-Key: &lt;votre_clé&gt;</code>
          </div>
          {[
            { method:'GET',    path:'/ptl/catalogue',                      desc:'Catalogue complet (tarifs public + B2B)' },
            { method:'GET',    path:'/ptl/catalogue/{id}',                 desc:'Fiche produit + disponibilités 90j' },
            { method:'GET',    path:'/ptl/availability/{id}?date_start=&date_end=', desc:'Vérifie dispo (OBLIGATOIRE avant booking)' },
            { method:'POST',   path:'/ptl/availability/bulk',              desc:'Vérifie dispo multiple produits' },
            { method:'POST',   path:'/ptl/book',                           desc:'Crée réservation depuis PTL' },
            { method:'DELETE', path:'/ptl/book/{quote_id}',               desc:'Annule réservation PTL' },
            { method:'GET',    path:'/ptl/calendar?date_from=&date_to=',  desc:'Calendrier complet SE (livraisons + résa)' },
            { method:'GET',    path:'/ptl/zones',                          desc:'Zones de livraison + tarifs' },
          ].map(ep => (
            <div key={ep.path} className="card-premium p-4 flex items-start gap-3">
              <span className="text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0"
                style={{ background: ep.method==='GET' ? 'rgba(34,197,94,.1)' : ep.method==='POST' ? 'rgba(212,175,55,.1)' : 'rgba(239,68,68,.1)',
                  color: ep.method==='GET' ? '#16A34A' : ep.method==='POST' ? '#C58B19' : '#EF4444' }}>
                {ep.method}
              </span>
              <div>
                <code className="text-xs font-bold" style={{ color:'var(--ink)' }}>{ep.path}</code>
                <p className="text-xs mt-0.5" style={{ color:'var(--text-muted)' }}>{ep.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
